import { Box } from '@material-ui/core';
import * as React from 'react';
import styled from 'styled-components';
import { RowDataBaseType, TypedDataGridColDef } from './TypedDataGrid';

const StyledColumnInner = styled.div`
    padding: 10px 10px;
`;

const StyledColumn = styled.div<{ $column: TypedDataGridColDef }>`
    flex: ${props => props.$column.flex};
    text-align: ${props => props.$column.align};
`;

const StyledWrapper = styled.div<{ $bold: boolean }>`
    display: flex;
    ${props => props.$bold && 'font-weight: bold;'}
`;

export type GoogleAdsAdsReportTotalsRow = {
    cost: string | null;
    costPerConversion: string | null;
    impressions: string;
    interactions: string;
    conversions: string;
};

export type ColumnForFieldProps<RowData extends RowDataBaseType> = {
    child?: React.ReactNode;
    column: TypedDataGridColDef<RowData>;
};

export function ColumnForField<RowData extends RowDataBaseType>({
    child,
    column,
}: ColumnForFieldProps<RowData>): JSX.Element {
    return column.hide ? (
        <></>
    ) : (
        <StyledColumn $column={column as TypedDataGridColDef}>
            {child && <StyledColumnInner>{child}</StyledColumnInner>}
        </StyledColumn>
    );
}
ColumnForField.displayName = 'ColumnForField';

export type TypedDataGridTotalsFooterTotals<RowData extends RowDataBaseType> = Partial<
    Record<TypedDataGridColDef<RowData>['field'], React.ReactNode>
>;

export type TypedDataGridTotalsFooterColumnProps<RowData extends RowDataBaseType> = {
    column: TypedDataGridColDef<RowData>;
    totals: TypedDataGridTotalsFooterTotals<RowData>;
};

export function TypedDataGridTotalsFooterColumn<RowData extends RowDataBaseType>({
    column,
    totals,
}: TypedDataGridTotalsFooterColumnProps<RowData>): JSX.Element {
    if (column.field in totals) {
        return <ColumnForField child={totals[column.field]} column={column} />;
    }
    return <ColumnForField column={column} />;
}
TypedDataGridTotalsFooterColumn.displayName = 'GoogleAdsAdsReportTotalColumn';

export type TypedDataGridTotalsFooterProps<RowData extends RowDataBaseType> = {
    columns: Array<TypedDataGridColDef<RowData>>;
    totals: TypedDataGridTotalsFooterTotals<RowData>;
    bold?: boolean;
    'data-testid'?: string;
};

export function TypedDataGridTotalsFooter<RowData extends RowDataBaseType>({
    bold = true,
    columns,
    totals,
    ...props
}: TypedDataGridTotalsFooterProps<RowData>): JSX.Element {
    return (
        <Box {...props}>
            <StyledWrapper $bold={bold}>
                {columns.map(column => (
                    <TypedDataGridTotalsFooterColumn column={column} key={column.field.toString()} totals={totals} />
                ))}
            </StyledWrapper>
        </Box>
    );
}
TypedDataGridTotalsFooter.displayName = 'TypedDataGridTotalsFooter';
